import { useCallback, useContext } from "react";
import {
    CheckoutActionTypes,
    CheckoutContext,
    getBridgeSdk,
    NEWRELIC_TABLES,
    recordNREvent,
} from "@swiggy-private/checkout-core";

// hooks
import { useBillImages } from "./UseBillImages";

// types
import { TOAST_TYPE } from "@swiggy-private/payment-types";

import { BILL_IMAGE_WEBVIEW_CONFIG, BILL_IMAGE_WEBVIEW_ERROR_MESSAGES } from "@payment-client/constants/BillImages";

interface UseBillImageWebviewReturn {
    openBillImageWebview: (onBillImagesUpdated?: VoidFunction) => Promise<void>;
}

export const useBillImageWebview = (): UseBillImageWebviewReturn => {
    const { updateBillImages } = useBillImages();

    const { setCheckoutState } = useContext(CheckoutContext);

    const openBillImageWebview = useCallback(
        async (onBillImagesUpdated?: VoidFunction): Promise<void> => {
            try {
                const response = await getBridgeSdk()?.openWebviewAndSyncData?.({
                    source: BILL_IMAGE_WEBVIEW_CONFIG.SOURCE,
                    link: BILL_IMAGE_WEBVIEW_CONFIG.LINK,
                });

                // Extract and validate links
                const rawLinks = (response as unknown as { data: { s3Links: string[] } })?.data?.s3Links;
                const s3Links = Array.isArray(rawLinks)
                    ? rawLinks.filter((u: unknown): u is string => typeof u === "string" && u.length > 0)
                    : [];

                if (s3Links.length === 0) {
                    // user closed the webview or invalid payload
                    return;
                }
                await updateBillImages({ uploadedFileURLs: s3Links });

                // Call the callback on successful submission
                onBillImagesUpdated?.();
            } catch (err) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.OPEN_BILL_IMAGE_WEBVIEW_ERROR,
                    error: err instanceof Error ? err.name : JSON.stringify(err),
                });

                setCheckoutState({
                    type: CheckoutActionTypes.GENERIC_PAYMENT_TOAST,
                    payload: {
                        message: BILL_IMAGE_WEBVIEW_ERROR_MESSAGES.UPLOAD_ERROR,
                        type: TOAST_TYPE.INFO_FILLED,
                    },
                });
            }
        },
        [updateBillImages, setCheckoutState],
    );

    return {
        openBillImageWebview,
    };
};
